import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePdfConfig } from '../context/PdfConfigContext';
import { renderReportPdf } from '../services/api';

// DOCUMENT_MAP kept for PdfOptionsModal which still needs client-side PDFViewer preview
import ReceiptDocument            from './lib/documents/ReceiptDocument';
import PaymentVoucherDocument     from './lib/documents/PaymentVoucherDocument';
import BalanceSheetDocument       from './lib/documents/BalanceSheetDocument';
import IncomeExpenseDocument      from './lib/documents/IncomeExpenseDocument';
import TrialBalanceDocument       from './lib/documents/TrialBalanceDocument';
import CategoryFundDocument       from './lib/documents/CategoryFundDocument';
import DayBookDocument            from './lib/documents/DayBookDocument';
import CashBookDocument           from './lib/documents/CashBookDocument';
import BankBookDocument           from './lib/documents/BankBookDocument';
import GoldRegisterDocument       from './lib/documents/GoldRegisterDocument';
import ItemRegisterDocument       from './lib/documents/ItemRegisterDocument';
import LedgerDocument             from './lib/documents/LedgerDocument';
import PledgeAgingDocument        from './lib/documents/PledgeAgingDocument';
import OfferingsReportDocument     from './lib/documents/OfferingsReportDocument';
import MemberReportDocument       from './lib/documents/MemberReportDocument';
import PendingBalanceDocument     from './lib/documents/PendingBalanceDocument';
import MemberLedgerDocument       from './lib/documents/MemberLedgerDocument';
import OwnerLedgerDocument        from './lib/documents/OwnerLedgerDocument';
import FamilyReportDocument       from './lib/documents/FamilyReportDocument';
import AnonymousDonationsDocument from './lib/documents/AnonymousDonationsDocument';
import FullInfoDocument           from './lib/documents/FullInfoDocument';
import MemberDirectoryDocument    from './lib/documents/MemberDirectoryDocument';
import LedgerAccountsDocument    from './lib/documents/LedgerAccountsDocument';
import AddressBookDocument       from './lib/documents/AddressBookDocument';
import DerasarMemberListDocument from './lib/documents/DerasarMemberListDocument';

export const DOCUMENT_MAP = {
  receipt:             ReceiptDocument,
  paymentVoucher:      PaymentVoucherDocument,
  offeringAging:       PledgeAgingDocument,
  balanceSheet:        BalanceSheetDocument,
  incomeExpense:       IncomeExpenseDocument,
  trialBalance:        TrialBalanceDocument,
  categoryFund:        CategoryFundDocument,
  dayBook:             DayBookDocument,
  cashBook:            CashBookDocument,
  bankBook:            BankBookDocument,
  goldRegister:        GoldRegisterDocument,
  itemRegister:        ItemRegisterDocument,
  ledger:              LedgerDocument,
  offerings:           OfferingsReportDocument,
  memberContributions: MemberReportDocument,
  pendingBalance:      PendingBalanceDocument,
  memberLedger:        MemberLedgerDocument,
  ownerLedger:         OwnerLedgerDocument,
  familyReport:        FamilyReportDocument,
  receiptsBook:        FullInfoDocument,
  anonymousDonations:  AnonymousDonationsDocument,
  memberDirectory:     MemberDirectoryDocument,
  ledgerBalance:       LedgerAccountsDocument,
  addressBook:         AddressBookDocument,
  derasarMemberList:   DerasarMemberListDocument,
};

export const hasPdfDefaults = (configs, type) => !!(configs && configs[type]);

export function usePdfDownload(type) {
  const { configs, resolveConfig } = usePdfConfig();
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async (documentProps) => {
    if (!documentProps) return;
    setDownloading(true);
    try {
      const { theme, orientation, sections } = resolveConfig(type);
      const res = await renderReportPdf({
        type,
        data:        documentProps,
        theme,
        orientation,
        sections,
        filters:     documentProps?.filters || '',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${type}-${Date.now()}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('PDF generation failed', err);
      toast.error('PDF generation failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [type, resolveConfig]);

  return { download, downloading, hasSaved: hasPdfDefaults(configs, type) };
}
